import { useState, useEffect } from 'react'
import {
  subscriptions,
  availableProducts,
  getItemPrice,
} from '../data/mockSubscriptionData'
export interface FormData {
  status: string
  nextPayment: string
  pauseDuration: string
  shippingFirstName: string
  shippingLastName: string
  shippingAddress1: string
  shippingAddress2: string
  shippingCity: string
  shippingState: string
  shippingPostCode: string
  shippingCountry: string
  deliveryInstructions: string
  billingFirstName: string
  billingLastName: string
  billingAddress1: string
  billingAddress2: string
  billingCity: string
  billingState: string
  billingPostCode: string
  billingCountry: string
  newNote: string
  preference: string
  bottleCount: string
  frequency: string
  shippingMethod: string
}
export function useSubscriptionForm(id: string | undefined) {
  const [isEditing, setIsEditing] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [hasChanges, setHasChanges] = useState(false)

  // Form state
  const [formData, setFormData] = useState<FormData>({
    status: '',
    nextPayment: '',
    pauseDuration: '',
    shippingFirstName: '',
    shippingLastName: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingState: '',
    shippingPostCode: '',
    shippingCountry: '',
    deliveryInstructions: '',
    billingFirstName: '',
    billingLastName: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingState: '',
    billingPostCode: '',
    billingCountry: '',
    newNote: '',
    preference: 'Choose your own',
    bottleCount: '6',
    frequency: 'Every 6 Months',
    shippingMethod: 'Flat Rate Shipping',
  })
  const [initialFormData, setInitialFormData] = useState<FormData>(formData)

  // Product selection state
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, number>
  >({})
  const [initialSelectedProducts, setInitialSelectedProducts] = useState<
    Record<string, number>
  >({})
  useEffect(() => {
    // Find subscription by ID
    const found = subscriptions.find((s: any) => s.id === id)
    if (found) {
      setSubscription(found)
      const [firstName, lastName] = found.customerName.split(' ')

      // Initialize selected products from subscription items
      const initialProducts: Record<string, number> = {}
      found.items?.forEach((item: any) => {
        const product = availableProducts.find((p) =>
          item.name.includes(p.name),
        )
        if (product) {
          initialProducts[product.id] = item.quantity
        }
      })
      setSelectedProducts(initialProducts)
      setInitialSelectedProducts(initialProducts)
      const initialData: FormData = {
        status: found.status,
        nextPayment: found.nextPayment,
        pauseDuration: '',
        // Shipping address - Realistic Australian address
        shippingFirstName: firstName || 'Alice',
        shippingLastName: lastName || 'Johnson',
        shippingAddress1: 'Craigends Ln',
        shippingAddress2: '',
        shippingCity: 'Tamworth',
        shippingState: 'NSW',
        shippingPostCode: '2340',
        shippingCountry: 'Australia',
        deliveryInstructions: 'Please leave at front door if no one is home',
        // Billing address - Same as shipping
        billingFirstName: firstName || 'Alice',
        billingLastName: lastName || 'Johnson',
        billingAddress1: 'Craigends Ln',
        billingAddress2: '',
        billingCity: 'Tamworth',
        billingState: 'NSW',
        billingPostCode: '2340',
        billingCountry: 'Australia',
        newNote: '',
        preference: 'Choose your own',
        bottleCount: found.subscriptionTitle.includes('12')
          ? '12'
          : found.subscriptionTitle.includes('3')
            ? '3'
            : '6',
        frequency: found.subscriptionTitle.includes('Month')
          ? 'Every Month'
          : found.subscriptionTitle.includes('3 Months')
            ? 'Every 3 Months'
            : 'Every 6 Months',
        shippingMethod: 'Flat Rate Shipping',
      }
      setFormData(initialData)
      setInitialFormData(initialData)
    }
    setLoading(false)
  }, [id])

  // Check for changes whenever formData or selectedProducts change
  useEffect(() => {
    if (!isEditing) {
      setHasChanges(false)
      return
    }

    // Compare formData with initialFormData
    const formHasChanges =
      JSON.stringify(formData) !== JSON.stringify(initialFormData)

    // Compare selectedProducts with initialSelectedProducts
    const productsHaveChanges =
      JSON.stringify(selectedProducts) !==
      JSON.stringify(initialSelectedProducts)
    setHasChanges(formHasChanges || productsHaveChanges)
  }, [
    formData,
    selectedProducts,
    initialFormData,
    initialSelectedProducts,
    isEditing,
  ])
  const handleSave = () => {
    // Update subscription items based on selectedProducts
    const newItems = Object.entries(selectedProducts)
      .filter(([_, qty]) => qty > 0)
      .map(([pid, qty]) => {
        const product = availableProducts.find((p) => p.id === pid)
        return {
          name: product?.name || 'Unknown Wine',
          quantity: qty,
        }
      })
    setSubscription((prev: any) => ({
      ...prev,
      status: formData.status,
      nextPayment: formData.nextPayment,
      items: newItems,
      subscriptionTitle: `${formData.bottleCount} Bottles ${formData.frequency}`,
    }))
    setIsEditing(false)
    setHasChanges(false)

    // Update initial values after save
    setInitialFormData(formData)
    setInitialSelectedProducts(selectedProducts)
  }
  // Sets the next payment as saved (e.g. putting a member back on their pack's schedule)
  const applyNextPayment = (value: string) => {
    setSubscription((prev: any) => (prev ? { ...prev, nextPayment: value } : prev))
    setFormData((prev) => ({ ...prev, nextPayment: value }))
    setInitialFormData((prev) => ({ ...prev, nextPayment: value }))
  }
  const handleCancel = () => {
    // Revert to initial values
    setFormData(initialFormData)
    setSelectedProducts(initialSelectedProducts)
    setIsEditing(false)
    setHasChanges(false)
  }
  const updateProductQuantity = (productId: string, delta: number) => {
    setSelectedProducts((prev) => {
      const current = prev[productId] || 0
      const updated = Math.max(0, current + delta)
      return {
        ...prev,
        [productId]: updated,
      }
    })
  }

  // Calculate totals
  const currentTotalBottles = Object.values(selectedProducts).reduce(
    (a, b) => a + b,
    0,
  )
  const targetBottles = parseInt(formData.bottleCount)
  const isBottleCountCorrect = currentTotalBottles === targetBottles
  const subtotal = isEditing
    ? Object.entries(selectedProducts).reduce((acc, [pid, qty]) => {
        const product = availableProducts.find((p) => p.id === pid)
        return acc + (product?.price || 0) * qty
      }, 0)
    : subscription?.items?.reduce(
        (acc: number, item: any) =>
          acc + getItemPrice(item.name) * item.quantity,
        0,
      ) || 0
  const discountAmount = subscription
    ? (subtotal * subscription.discountPercent) / 100
    : 0
  const total = subtotal - discountAmount
  return {
    loading,
    subscription,
    isEditing,
    setIsEditing,
    hasChanges,
    formData,
    setFormData,
    selectedProducts,
    handleSave,
    handleCancel,
    applyNextPayment,
    updateProductQuantity,
    currentTotalBottles,
    targetBottles,
    isBottleCountCorrect,
    subtotal,
    discountAmount,
    total,
  }
}
